import { useQuery } from '@tanstack/react-query';
import { Tooltip as TooltipPrimitive } from 'radix-ui';

import { chatDebugStore } from '@/features/chat-debug/chat-debug.store';
import {
  ZERO_COST_BREAKDOWN,
  sumCostBreakdowns,
} from '@/features/chat-debug/chat-debug.types';
import {
  CostSummaryTable,
  formatUsd as formatUsd6,
} from '@/features/chat-debug/llm-costs/llm-costs.viewer';
import { CostTokensStrip } from '@/features/chat/cost-footer/ComposerCostFooter';
import { classList } from '@/utils/cn';

import { reportCostQuery } from './report-cost.query';

import type { ReactNode } from 'react';

export interface ReportCostFooterProps {
  /** Hidden chat that produced the report */
  readonly chatId: string;
  readonly className?: string;
}

// Internal-only cost strip for a routine run, mirroring the chat composer cost
// footer. Callers gate rendering on internal user + the cost footer setting.
export function ReportCostFooter({
  chatId,
  className,
}: ReportCostFooterProps): ReactNode {
  const { data: summary } = useQuery(reportCostQuery(chatId));
  if (!summary) return null;

  const grandTotalUsd = summary.grand_total_usd ?? 0;
  const turnsTotal = summary.turns.reduce(
    (acc, turn) => sumCostBreakdowns(acc, turn.total_cost),
    ZERO_COST_BREAKDOWN
  );
  const runTotal = summary.compaction_total
    ? sumCostBreakdowns(turnsTotal, summary.compaction_total)
    : turnsTotal;
  const model = summary.latest_step?.model ?? null;

  const handleOpenDebug = (): void => {
    chatDebugStore.setActiveChatId(chatId);
    const session = chatDebugStore.active;
    if (!session) return;
    if (session.messages.length === 0) {
      session.fetchTracesForChat();
    }
    session.setIsOpen(true);
  };

  return (
    <div
      data-testid="report-cost-footer"
      data-chat-id={chatId}
      data-total-usd={grandTotalUsd.toFixed(6)}
      className={classList(
        'flex flex-row items-center',
        'py-1',
        'text-[11px] text-foreground-muted',
        'select-none',
        className
      )}
    >
      <TooltipPrimitive.Root delayDuration={150}>
        <TooltipPrimitive.Trigger asChild>
          <button
            type="button"
            onClick={handleOpenDebug}
            className={classList(
              'flex flex-row items-center gap-3',
              'hover:text-foreground-subtle transition-colors',
              'cursor-pointer'
            )}
            aria-label="Open routine run cost details"
          >
            <CostTokensStrip breakdown={runTotal} totalUsd={grandTotalUsd} />
          </button>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="top"
            sideOffset={6}
            className={classList(
              'z-50',
              'rounded-lg smoothing-default',
              'border border-border-strong bg-surface shadow-sm',
              'p-3 text-xs text-foreground-subtle',
              'min-w-[280px]'
            )}
          >
            <div className="flex items-center justify-between gap-4 pb-2 text-[11px] text-foreground-muted">
              <span className="truncate">{model ?? 'Run cost'} · this run</span>
              <span className="tabular-nums">{formatUsd6(grandTotalUsd)}</span>
            </div>
            <CostSummaryTable breakdown={runTotal} />
            <div className="text-[10px] text-foreground-muted pt-2">
              Click to open cost details in the debug sidebar.
            </div>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </div>
  );
}
